import * as React from "react";
import { useMemo } from "react";
import { useFeatureFlags } from "@/hooks/use-feature-flags";
import { useFeatureAccess } from "@/hooks/use-feature-access";
import { useOrganization } from "@/hooks/use-organization";
import { useSuperAdmin } from "@/hooks/use-super-admin";
import { useStripeConnect } from "@/hooks/use-stripe-connect";
import { useBillingModel } from "@/hooks/use-billing-model";
import { useSubscription, isTeamTier } from "@/hooks/use-subscription";
import { useSidebarFavorites } from "@/hooks/use-sidebar-favorites";
import { SIDEBAR_NAV_CONFIG, SidebarNavSection, SectionNavItem, NavItem } from "@/config/navigation";

export const FAVORITES_SECTION_ID = "overview";

export function useSidebarNavigation() {
  const { hasFeature } = useFeatureFlags();
  const { hasFeature: hasTierFeature, isLoading: tierLoading } = useFeatureAccess();
  const { isOrgAdminOrOwner } = useOrganization();
  const { isSuperAdmin } = useSuperAdmin();
  const { isConnected: isStripeConnected } = useStripeConnect();
  const { config: billingConfig } = useBillingModel();
  const { subscription } = useSubscription();
  const { favorites, isFavorite, toggleFavorite } = useSidebarFavorites();

  const filteredSections = useMemo(() => {
    // Filter function for individual nav items
    const filterItem = (item: NavItem): boolean => {
      if (item.featureFlag && !hasFeature(item.featureFlag)) return false;
      if (item.requiresAdmin && !isOrgAdminOrOwner) return false;
      if (item.requiresSuperAdmin && !isSuperAdmin) return false;
      if (item.requiresStripeConnect && !isStripeConnected) return false;
      if (item.requiresBillingFeature && !billingConfig.features[item.requiresBillingFeature]) return false;
      if (item.requiresTeamTier && (!subscription?.tier || !isTeamTier(subscription.tier))) return false;
      // Tier / addon gate (from FEATURE_GATES). Skip while still loading so the nav
      // doesn't flicker as the subscription query resolves.
      if (item.requiresTierFeature && !tierLoading && !hasTierFeature(item.requiresTierFeature)) return false;
      return true;
    };

    // First pass: filter sections and their items
    const sections = SIDEBAR_NAV_CONFIG
      .filter((section) => {
        if (section.requiresAdmin && !isOrgAdminOrOwner) return false;
        if (section.requiresSuperAdmin && !isSuperAdmin) return false;
        return true;
      })
      .map((section) => ({
        ...section,
        items: section.items
          .filter(filterItem)
          .map((item) => ({
            ...item,
            children: item.children?.filter(filterItem),
          })),
      }))
      .filter((section) => section.items.length > 0);

    // Build a lookup map of all visible items (excluding permanent favorites items)
    const itemMap = new Map<string, SectionNavItem>();
    for (const section of sections) {
      if (section.id === FAVORITES_SECTION_ID) continue;
      for (const item of section.items) {
        itemMap.set(item.id, item);
      }
    }

    // Inject favorited items into the Favorites section
    const favoritesSection = sections.find((s) => s.id === FAVORITES_SECTION_ID);
    if (favoritesSection) {
      const permanentIds = new Set(
        favoritesSection.items.filter((i) => i.permanent).map((i) => i.id),
      );
      const favoritedItems: SectionNavItem[] = [];
      for (const favId of favorites) {
        if (permanentIds.has(favId)) continue; // already permanent
        const item = itemMap.get(favId);
        if (item) {
          favoritedItems.push({ ...item, permanent: false });
        }
      }
      favoritesSection.items = [...favoritesSection.items, ...favoritedItems];
    }

    return sections;
  }, [hasFeature, hasTierFeature, tierLoading, isOrgAdminOrOwner, isSuperAdmin, isStripeConnected, billingConfig, subscription, favorites]);

  return { sections: filteredSections, isFavorite, toggleFavorite };
}
